import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useAppTranslation } from "../TranslationContext";
import s from './Register.module.css';
import { useTranslation } from 'react-i18next';

const parseApiError = (data) => {
  if (!data) return null;
  if (typeof data === 'string') return data;
  const knownFields = ['username', 'email', 'password1', 'password2', 'non_field_errors', 'detail'];
  for (const field of knownFields) {
    const val = data[field];
    if (val) return Array.isArray(val) ? val[0] : String(val);
  }
  const first = Object.values(data)[0];
  if (first) return Array.isArray(first) ? first[0] : String(first);
  return null;
};

export const Register = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [token, setToken] = useState('');
  const [userId, setUsersId] = useState('');
  const { t } = useAppTranslation();
  const { i18n } = useTranslation();

  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password1: '',
    password2: '',
    first_name: '',
    last_name: '',
    til: '',
    ilmiy_daraja: '',
    otasining_ismi: '',
    description: '',
  });

  const [showPassword, setShowPassword] = useState(false);
  const [message, setMessage] = useState('');
  const [degrees, setDegrees] = useState([]);

  useEffect(() => {
    const fetchDegrees = async () => {
      try {
        const res = await fetch('https://api.eduskill.uz/api/ilmiy-darajalar/');
        const data = await res.json();
        setDegrees(data);
      } catch (err) {
        console.error("Ошибка при получении степеней:", err);
      }
    };
    fetchDegrees();
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setMessage('');
    try {
      const res = await fetch('https://api.eduskill.uz/api/v1/dj-rest-auth/registration/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: formData.username,
          email: formData.email,
          password1: formData.password1,
          password2: formData.password2,
        }),
      });

      let data = {};
      try { data = await res.json(); } catch {}

      if (!res.ok) {
        setMessage(parseApiError(data) || (t('regError') || 'Ошибка при регистрации'));
        return;
      }

      let tokenValue = data.key ?? data.access ?? data.token ?? '';
      if (!tokenValue) {
        const loginRes = await fetch('https://api.eduskill.uz/api/v1/dj-rest-auth/login/', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username: formData.username, password: formData.password1 }),
        });
        const loginData = await loginRes.json();
        if (!loginRes.ok) {
          setMessage(parseApiError(loginData) || (t('loginAfterRegError') || 'Регистрация прошла, но не удалось войти'));
          return;
        }
        tokenValue = loginData.key ?? loginData.access ?? loginData.token ?? '';
      }

      if (!tokenValue) {
        setMessage(t('tokenError') || 'Не удалось получить токен авторизации');
        return;
      }

      setToken(tokenValue);

      try {
        const userRes = await fetch('https://api.eduskill.uz/api/v1/dj-rest-auth/user/', {
          headers: { Authorization: `Token ${tokenValue}` },
        });
        const user = await userRes.json();
        if (user?.pk) setUsersId(user.pk);
      } catch {}

      setStep(2);
    } catch (err) {
      setMessage(t('serverError') || 'Ошибка сервера. Попробуйте позже.');
    }
  };

  const handleUpdateNames = async (e) => {
    e.preventDefault();
    setMessage('');
    try {
      const res = await fetch('https://api.eduskill.uz/api/v1/dj-rest-auth/user/', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Token ${token}` },
        body: JSON.stringify({ first_name: formData.first_name, last_name: formData.last_name }),
      });

      if (res.ok) {
        setStep(3);
      } else {
        const errData = await res.json();
        setMessage(parseApiError(errData) || (t('nameUpdateError') || 'Ошибка при обновлении имени'));
      }
    } catch {
      setMessage(t('serverError') || 'Ошибка сервера');
    }
  };

  const handleCreateProfile = async (e) => {
    e.preventDefault();
    setMessage('');
    try {
      const res = await fetch('https://api.eduskill.uz/api/custom-users/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Token ${token}` },
        body: JSON.stringify({
          user: userId,
          til: Number(formData.til),
          ilmiy_daraja: Number(formData.ilmiy_daraja),
          otasining_ismi: formData.otasining_ismi,
          description: formData.description,
        }),
      });

      if (res.ok) {
        const user = {
          id: userId,
          til: formData.til,
          username: formData.username,
          first_name: formData.first_name,
          last_name: formData.last_name,
          email: formData.email,
        };
        login({ token, user });
        localStorage.setItem('token', token);
        navigate('/dashboard');
      } else {
        const errData = await res.json();
        setMessage(parseApiError(errData) || (t('profileCreateError') || 'Ошибка при создании профиля'));
      }
    } catch {
      setMessage(t('serverError') || 'Ошибка сервера');
    }
  };

  const handleSkip = () => {
    login({
      token,
      user: {
        id: userId,
        username: formData.username,
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
      },
    });
    localStorage.setItem('token', token);
    navigate('/dashboard');
  };

  return (
    <form onSubmit={step === 1 ? handleRegister : step === 2 ? handleUpdateNames : handleCreateProfile}>
      <div className={s.wrapper}>
        <div className={s.registerBox}>

          {/* Step indicator */}
          <div className={s.stepIndicator}>
            {[1, 2, 3].map(n => (
              <React.Fragment key={n}>
                <div className={`${s.stepDot} ${step === n ? s.stepDotActive : step > n ? s.stepDotDone : ''}`}>
                  {step > n ? '✓' : n}
                </div>
                {n < 3 && <div className={`${s.stepLine} ${step > n ? s.stepLineDone : ''}`} />}
              </React.Fragment>
            ))}
          </div>

          <h2 className={s.title}>
            {step === 1 ? t('step1') : step === 2 ? t('step2') : t('step3')}
          </h2>

          <div className={s.inputGroup}>
            {step === 1 && (
              <>
                <label className={s.labelTop} htmlFor="username">{t('Username')}</label>
                <input name="username" placeholder={t('enterUsername')} value={formData.username} onChange={handleChange} required className={s.input} />

                <label className={s.labelTop} htmlFor="email">{t('Email')}</label>
                <input name="email" type="email" placeholder={t('enterEmail')} value={formData.email} onChange={handleChange} required className={s.input} />

                <label className={s.labelTop} htmlFor="password1">{t('Password')}</label>
                <div style={{ position: 'relative' }}>
                  <input name="password1" type={showPassword ? 'text' : 'password'} placeholder={t('enterPassword')} value={formData.password1} onChange={handleChange} required className={s.input} style={{ paddingRight: 40 }} />
                  <button type="button" onClick={() => setShowPassword(v => !v)} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 16 }} aria-label={showPassword ? (t('hidePassword') || 'Скрыть') : (t('showPassword') || 'Показать')}>
                    {showPassword ? '🙈' : '👁'}
                  </button>
                </div>

                <label className={s.labelTop} htmlFor="password2">{t('repeatPassword')}</label>
                <input name="password2" type={showPassword ? 'text' : 'password'} placeholder={t('enterRepeatPass')} value={formData.password2} onChange={handleChange} required className={s.input} />
              </>
            )}

            {step === 2 && (
              <>
                <label className={s.labelTop} htmlFor="first_name">{t('FirstName')}</label>
                <input name="first_name" placeholder={t('enterFirstName')} value={formData.first_name} onChange={handleChange} required className={s.input} />

                <label className={s.labelTop} htmlFor="last_name">{t('LastName')}</label>
                <input name="last_name" placeholder={t('enterLastName')} value={formData.last_name} onChange={handleChange} required className={s.input} />
              </>
            )}

            {step === 3 && (
              <>
                <label className={s.labelStep3}>
                  <span>{t('Patronymic')}</span>
                  <input name="otasining_ismi" placeholder={t('enterPatronymic')} value={formData.otasining_ismi} onChange={handleChange} className={s.input} />
                </label>

                <div className={s.labelStep3}>
                  <span>{t('language')}</span>
                  <select name="til" value={formData.til} onChange={handleChange} required className={s.inputStep3}>
                    <option value="">{t('SelectLang')}</option>
                    <option value="1">O'zbek tili</option>
                    <option value="2">Русский язык</option>
                    <option value="3">English</option>
                  </select>
                </div>

                <div className={s.labelStep3}>
                  <span>{t('AcadDegree')}</span>
                  <select name="ilmiy_daraja" value={formData.ilmiy_daraja} onChange={handleChange} required className={s.inputStep3}>
                    <option value="">{t('enterAcadDegree')}</option>
                    {degrees.map(degree => {
                      let name;
                      switch (i18n.language) {
                        case 'ru': name = degree.nomi_ru; break;
                        case 'en': name = degree.nomi_en; break;
                        default: name = degree.nomi_uz;
                      }
                      return <option key={degree.id} value={degree.id}>{name}</option>;
                    })}
                  </select>
                </div>

                <div className={s.labelStep3}>
                  <span>{t('about')}</span>
                  <textarea name="description" placeholder={t('enterAbout')} value={formData.description} onChange={handleChange} className={s.input} />
                </div>
              </>
            )}
          </div>

          {message && (
            <p style={{ color: 'crimson', fontSize: 14, marginBottom: 12, textAlign: 'center' }}>
              {message}
            </p>
          )}

          <div className={s.buttonGroup}>
            {step > 1 && (
              <button type="button" className="register__btn btn__passive" onClick={() => { setMessage(''); setStep(s => s - 1); }}>
                {t('back') || '← Назад'}
              </button>
            )}
            {step === 3 && (
              <button type="button" className="register__btn btn__passive btn__skip" onClick={handleSkip}>
                {t('skip')}
              </button>
            )}
          </div>

          <div className={s.buttonGroup}>
            <button type="submit" className={s.loginBtn}>
              {step === 1 ? t('register') : step === 2 ? t('next2') : t('finish2')}
            </button>
            {step === 1 && <Link to="/login" className={s.registerBtn}>{t('login')}</Link>}
          </div>

        </div>
      </div>
    </form>
  );
};
